import { LAYER_FORMAT_TYPE } from '@/constants/layer';
import { canPolicyActionEdit } from '@/constants/policy';
import { queryClient } from '@/graphql/client';
import { TransformSceneEntityMutationVariables } from '@/graphql/codegen/graphql';
import { TRANSFORM_SCENE_ENTITY } from '@/graphql/mutations';
import { request } from '@/graphql/request';
import { useFetchSceneEntities } from '@/hooks/useFetchSceneEntities';
import { useFetchSceneEntityPermissions } from '@/hooks/useFetchSceneEntityPermissions';
import { useExplore } from '@/stores/explore';
import { AnnotationGroup, Layer, LayerGroup, PhotoGroup, useViewer } from '@/stores/viewer';
import { cn } from '@/utils/classname';
import { getShareLinkToken } from '@/utils/shareLink';
import { Button, toast } from '@skand/ui';
import {
  Cartographic,
  Model3D,
  ModelNode,
  SceneNode,
  toCartesian,
  toCartographic,
  toDegrees,
  toEuler as toEulerViewer,
  toQuaternion,
  toRadians,
} from '@skand/viewer-component-v2';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { Euler, Quaternion, Vector3 } from 'three';
import { OpacityControl } from './OpacityControl';
import { PositionControl } from './PositionControl';
import { TransformControl } from './TransformControl';

export interface LayeringTabProps {
  layer: LayerGroup | Layer | PhotoGroup | AnnotationGroup;
}

export const LayeringTab = ({ layer }: LayeringTabProps) => {
  const hasShareLinkToken = getShareLinkToken();

  const model = layer.sceneNode as SceneNode;
  const projectId = useExplore(state => state.projectId);
  const positionCache = useViewer(state => state.positionCache);
  const defaultTransformCache = useViewer(state => state.defaultTransformCache);

  const [initialPosition, initialRotation] = useMemo(() => {
    const cached = positionCache.get(layer.id);
    if (cached) {
      return cached;
    } else {
      const position = model.getPosition();
      const rotation = model.getRotation();
      useViewer.setState(state => ({
        positionCache: new Map(state.positionCache.set(layer.id, [position, rotation])),
      }));
      return [position, rotation];
    }
  }, [layer.id, model, positionCache]);

  const currentPosition = model.getPosition();
  const currentRotation = model.getRotation();

  // Wrapper function to compute true Euler rotation by offsetting the model's default rotation
  //
  // NOTE: This is important for georeferenced models like IFCs and OBJs, which are pre-rotated on load
  // This ensures that the default rotation corresponds to Euler angles (0, 0, 0)
  const toEuler = (cartographic: Cartographic, rotation: Quaternion) => {
    const defaultTransform = defaultTransformCache.get(layer.id) ?? [
      new Vector3(),
      new Quaternion(),
    ];
    const defaultRotation = defaultTransform[1];

    const trueRotation = rotation.clone().multiply(defaultRotation.clone().invert());
    return toEulerViewer(cartographic, trueRotation);
  };

  // Separate state for responsive text input
  const [cartographic, setCartographic] = useState(toCartographic(currentPosition));
  const [euler, setEuler] = useState(toEuler(cartographic, currentRotation));

  const { getSceneEntityPermission } = useFetchSceneEntityPermissions();
  const permission = getSceneEntityPermission(layer.sceneEntityId);
  const canEdit = canPolicyActionEdit(permission);

  // Transform mutation
  const { mutate } = useMutation({
    mutationFn: (variables: TransformSceneEntityMutationVariables) => {
      return request(TRANSFORM_SCENE_ENTITY, variables);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(useFetchSceneEntities.getSceneEntityQueryKey(projectId));
      toast({
        type: 'success',
        message: `Successfully moved entity '${layer.name}'.`,
        lifespan: 5000,
      });
    },
  });

  // Save the position and rotation of the layer
  const updateTransform = useCallback(
    (position: Vector3, rotation: Quaternion) => {
      if (projectId) {
        model.setPosition(position);
        model.setRotation(rotation);

        const localPosition = model.getLocalPosition();
        const localRotation = model.getLocalRotation();
        mutate({
          sceneEntityId: layer.sceneEntityId,
          projectId,
          position: { x: localPosition.x, y: localPosition.y, z: localPosition.z },
          rotation: {
            x: localRotation.x,
            y: localRotation.y,
            z: localRotation.z,
            w: localRotation.w,
          },
        });
      }
    },
    [layer.sceneEntityId, model, mutate, projectId],
  );

  // Set the cartesian transform of the model
  const setCartesianTransform = (position: Vector3, rotation: Quaternion) => {
    const cartographic = toCartographic(position);
    const euler = toEuler(cartographic, rotation);

    setCartographic(cartographic);
    setEuler(euler);
    updateTransform(position, rotation);
  };

  // Set the cartographic position of the model
  const setCartographicPosition = (longitude: number, latitude: number, height: number) => {
    const cartographic = new Cartographic(toRadians(longitude), toRadians(latitude), height);
    const cartesian = toCartesian(cartographic);
    const euler = toEuler(cartographic, currentRotation);

    setCartographic(cartographic);
    setEuler(euler);
    updateTransform(cartesian, currentRotation);
  };

  // Set the rotation of the model
  // Similar to toEuler, this will take into account the model's default rotation
  const setCartographicRotation = (x: number, y: number, z: number) => {
    const defaultTransform = defaultTransformCache.get(layer.id) ?? [
      new Vector3(),
      new Quaternion(),
    ];
    const defaultRotation = defaultTransform[1];

    // Apply the default rotation to the desired quaternion
    // This ensures (0,0,0) in the UI means "default orientation"
    const euler = new Euler(toRadians(x), toRadians(y), toRadians(z));
    const quaternion = toQuaternion(cartographic, euler).multiply(defaultRotation);

    setEuler(euler);
    updateTransform(currentPosition, quaternion);
  };

  // Reset the position and rotation of the model
  const handleReset = () => {
    const cartographic = toCartographic(initialPosition);
    const euler = toEuler(cartographic, initialRotation);

    setCartographic(cartographic);
    setEuler(euler);
    updateTransform(initialPosition, initialRotation);
  };

  const handleClear = () => {
    const transformCache = defaultTransformCache.get(layer.id);

    if (transformCache) {
      const cartographic = toCartographic(transformCache[0]);
      const euler = toEuler(cartographic, transformCache[1]);

      setCartographic(cartographic);
      setEuler(euler);
      updateTransform(transformCache[0], transformCache[1]);
    }
  };

  return (
    <div className={cn('flex flex-col')}>
      {layer.sceneNode instanceof SceneNode && !hasShareLinkToken && canEdit && (
        <TransformControl model={layer.sceneNode} setTransform={setCartesianTransform} />
      )}
      {layer.sceneNode instanceof ModelNode &&
        !(layer.type === 'layer' && layer.formatType === LAYER_FORMAT_TYPE.IFC) &&
        !(layer.type === 'layer' && layer.formatType === LAYER_FORMAT_TYPE.GAUSSIAN_SPLAT) && (
          <OpacityControl model={layer.sceneNode.getModel() as Model3D} />
        )}
      {layer.sceneNode instanceof SceneNode && !hasShareLinkToken && (
        <>
          <PositionControl
            disabled={!canEdit}
            height={cartographic.height}
            latitude={toDegrees(cartographic.latitude)}
            longitude={toDegrees(cartographic.longitude)}
            setCartographicPosition={setCartographicPosition}
            setRotation={setCartographicRotation}
            x={toDegrees(euler.x)}
            y={toDegrees(euler.y)}
            z={toDegrees(euler.z)}
          />
          <div className={cn('flex gap-2 flex-col mt-3')}>
            <Button className="w-full" disabled={!canEdit} onClick={handleReset} size="s">
              Reset Position
            </Button>
          </div>
          <div className={cn('flex gap-2 flex-col mt-3')}>
            <Button className="w-full" disabled={!canEdit} onClick={handleClear} size="s">
              Clear Position
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
